import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useUserAccess } from '../../context/UserAccessContext';
import { fetchHubupMessageFormResponses } from '../../api/hubup';
import {
  effectiveOptionPriceTTC,
  parseQuestionRequired,
  questionColumnLabel,
} from '../../utils/hubupNotificationForm';

function formatParisDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function formatPrice(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(n);
}

function effectiveFormPriceTTC(form) {
  if (form?.priceTTC != null && form.priceTTC !== '') {
    const n = Number(form.priceTTC);
    return Number.isFinite(n) ? n : null;
  }
  if (form?.price != null && form.price !== '') {
    const n = Number(form.price);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function effectiveFormPriceHT(form) {
  if (form?.priceHT != null && form.priceHT !== '') {
    const n = Number(form.priceHT);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function effectiveResponseTotalTTC(row) {
  if (row?.totalPriceTTC != null && row.totalPriceTTC !== '') {
    const n = Number(row.totalPriceTTC);
    return Number.isFinite(n) ? n : null;
  }
  if (row?.totalPrice != null && row.totalPrice !== '') {
    const n = Number(row.totalPrice);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function effectiveResponseTotalHT(row) {
  if (row?.totalPriceHT != null && row.totalPriceHT !== '') {
    const n = Number(row.totalPriceHT);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function isResponsePaid(row) {
  return row?.paid !== false;
}

function paymentStatusMeta(row) {
  const paid = isResponsePaid(row);
  const ttc = effectiveResponseTotalTTC(row);
  const isFree = ttc == null || ttc === 0;
  if (paid) {
    return {
      label: isFree ? 'Gratuit' : 'Payé',
      className:
        'bg-emerald-100 text-emerald-900 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800',
    };
  }
  return {
    label: 'En attente',
    className:
      'bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800',
  };
}

function paymentStatusExportLabel(row) {
  const meta = paymentStatusMeta(row);
  const paymentId = row?.stancerPaymentId?.trim() || '';
  if (!isResponsePaid(row) && paymentId) {
    return `${meta.label} (${paymentId})`;
  }
  return meta.label;
}

function PaymentStatusBadge({ row }) {
  const meta = paymentStatusMeta(row);
  const paymentId = row?.stancerPaymentId?.trim() || null;
  return (
    <div className="space-y-1">
      <span
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${meta.className}`}
      >
        {meta.label}
      </span>
      {!isResponsePaid(row) && paymentId ? (
        <p
          className="text-[10px] font-mono text-muted-foreground break-all max-w-[140px]"
          title="ID paiement Stancer"
        >
          {paymentId}
        </p>
      ) : null}
    </div>
  );
}

function formatAnswerValue(question, answer) {
  if (!answer) {
    return parseQuestionRequired(question?.required) ? '—' : '';
  }
  if (question?.type === 'options') {
    const indexes = Array.isArray(answer.selectedOptionIndexes)
      ? answer.selectedOptionIndexes
      : [];
    if (!indexes.length) {
      return parseQuestionRequired(question?.required) ? '—' : '';
    }
    const options = Array.isArray(question.options) ? question.options : [];
    const labels = indexes
      .map((idx) => {
        const opt = options[idx];
        if (!opt) return null;
        const name = String(opt.optionName ?? '').trim();
        if (!name) return null;
        const ttc = effectiveOptionPriceTTC(opt);
        const price = ttc != null ? ` (${formatPrice(ttc)} TTC)` : '';
        return `${name}${price}`;
      })
      .filter(Boolean);
    return labels.length ? labels.join(', ') : '—';
  }
  if (question?.type === 'note') {
    const raw = answer.starRating;
    if (raw == null || raw === '') {
      return parseQuestionRequired(question?.required) ? '—' : '';
    }
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 1 && n <= 5) {
      return `${Math.round(n)}/5`;
    }
    return parseQuestionRequired(question?.required) ? '—' : '';
  }
  const text = String(answer.textValue ?? '').trim();
  if (!text) {
    return parseQuestionRequired(question?.required) ? '—' : '';
  }
  if (question?.textKind === 'number') {
    const n = Number(text.replace(',', '.'));
    if (Number.isFinite(n)) {
      return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 4 }).format(n);
    }
  }
  if (question?.textKind === 'date') {
    const raw = text.trim();
    const isoDay = /^\d{4}-\d{2}-\d{2}$/.test(raw);
    const parsed = new Date(isoDay ? `${raw}T12:00:00` : raw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
  }
  if (question?.textKind === 'phone') {
    const digits = text.replace(/\D/g, '');
    if (digits.length === 10) {
      return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    }
    if (digits.length === 11 && digits.startsWith('33')) {
      const local = `0${digits.slice(2)}`;
      return `+33 ${local.slice(1).replace(/(\d{2})(?=\d)/g, '$1 ').trim()}`;
    }
  }
  return text;
}

function csvEscape(value) {
  const s = String(value ?? '');
  if (/[",;\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function slugifyFilename(value) {
  return String(value || 'formulaire')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'formulaire';
}

function downloadFormResponsesCsv({ form, questions, rows }) {
  const headers = [
    'Date',
    'Visiteur',
    'Paiement',
    ...questions.map((q, i) => questionColumnLabel(q, i)),
    'Total HT',
    'Total TTC',
  ];

  const dataRows = rows.map((row) => [
    formatParisDate(row.createdAt),
    row.visitorId || '',
    paymentStatusExportLabel(row),
    ...questions.map((q, i) => formatAnswerValue(q, row.answersByIndex[i])),
    formatPrice(effectiveResponseTotalHT(row)),
    formatPrice(effectiveResponseTotalTTC(row)),
  ]);

  const lines = [headers, ...dataRows].map((line) =>
    line.map(csvEscape).join(';'),
  );
  const blob = new Blob(['\uFEFF', lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const stamp = new Date()
    .toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
  const a = document.createElement('a');
  a.href = url;
  a.download = `reponses-${slugifyFilename(form?.name)}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const HubupNotificationFormResponses = () => {
  const { groupId, messageId } = useParams();
  const location = useLocation();
  const { user } = useUserAccess();
  const isAdmin = user?.permission === 'admin';
  const canManageHubup = isAdmin || user?.hubup === true;

  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const notificationTitle =
    location.state?.notificationTitle?.trim() || null;

  const load = useCallback(async () => {
    if (!groupId || !messageId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchHubupMessageFormResponses(groupId, messageId);
      setForm(res?.data?.form ?? null);
      setResponses(res?.data?.responses ?? []);
    } catch (e) {
      setError(e?.message || 'Impossible de charger les réponses.');
      setForm(null);
      setResponses([]);
    } finally {
      setLoading(false);
    }
  }, [groupId, messageId]);

  useEffect(() => {
    if (canManageHubup) load();
  }, [canManageHubup, load]);

  const questions = useMemo(
    () => (Array.isArray(form?.questions) ? form.questions : []),
    [form],
  );

  const rows = useMemo(() => {
    return responses.map((row) => {
      const answersByIndex = {};
      for (const a of Array.isArray(row.answers) ? row.answers : []) {
        answersByIndex[Number(a.questionIndex)] = a;
      }
      return { ...row, answersByIndex };
    });
  }, [responses]);

  const basePriceHT = effectiveFormPriceHT(form);
  const basePriceTTC = effectiveFormPriceTTC(form);

  const paymentCounts = useMemo(() => {
    let paid = 0;
    let pending = 0;
    for (const row of responses) {
      if (isResponsePaid(row)) paid += 1;
      else pending += 1;
    }
    return { paid, pending };
  }, [responses]);

  if (!canManageHubup) {
    return (
      <main className="main-content">
        <p className="text-sm text-muted-foreground">
          Accès réservé à la gestion Hubup.
        </p>
      </main>
    );
  }

  const backHref = `/hubup/g/${groupId}/notifications`;
  const pageTitle = form?.name || 'Réponses du formulaire';

  return (
    <>
      <Helmet>
        <title>{pageTitle} — HubUP</title>
      </Helmet>
      <main className="main-content">
        <div className="mb-4">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="-ml-2 mb-4 text-muted-foreground hover:text-foreground"
          >
            <Link to={backHref}>
              <Icon name="ArrowLeft" size={16} className="mr-1.5" />
              Revenir aux notifications
            </Link>
          </Button>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
                {pageTitle}
              </h1>
              {notificationTitle ? (
                <p className="text-sm text-muted-foreground mb-1">
                  Notification :{' '}
                  <span className="text-foreground font-medium">{notificationTitle}</span>
                </p>
              ) : null}
              {form?.description ? (
                <p className="text-sm text-muted-foreground mb-2">{form.description}</p>
              ) : null}
              <p className="text-sm text-muted-foreground">
                {loading
                  ? 'Chargement…'
                  : `${responses.length} réponse${responses.length === 1 ? '' : 's'}`}
                {!loading && responses.length > 0 ? (
                  <>
                    {' · '}
                    {paymentCounts.paid} payée{paymentCounts.paid === 1 ? '' : 's'}
                    {paymentCounts.pending > 0
                      ? ` · ${paymentCounts.pending} en attente`
                      : ''}
                  </>
                ) : null}
                {basePriceHT != null || basePriceTTC != null ? (
                  <>
                    {' · '}
                    Prix de base :
                    {basePriceHT != null ? ` ${formatPrice(basePriceHT)} HT` : ''}
                    {basePriceTTC != null ? ` · ${formatPrice(basePriceTTC)} TTC` : ''}
                  </>
                ) : null}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              className="shrink-0 self-start sm:mt-1"
              disabled={loading || rows.length === 0}
              onClick={() => downloadFormResponsesCsv({ form, questions, rows })}
            >
              Exporter CSV
            </Button>
          </div>
        </div>

        {error ? (
          <p className="text-sm text-error mb-4">{error}</p>
        ) : null}

        <div className="bg-card rounded-lg shadow-elevation-2 border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Date</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Visiteur</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Paiement</th>
                  {questions.map((q, i) => (
                    <th
                      key={`q-${i}`}
                      className="px-4 py-3 font-medium min-w-[140px] max-w-[220px]"
                      title={questionColumnLabel(q, i)}
                    >
                      <span className="line-clamp-2 normal-case tracking-normal">
                        {questionColumnLabel(q, i)}
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 font-medium whitespace-nowrap text-right">
                    Total HT
                  </th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap text-right">
                    Total TTC
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={5 + questions.length}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      Chargement des réponses…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5 + questions.length}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      Aucune réponse pour le moment.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-border last:border-0 hover:bg-muted/20"
                    >
                      <td className="px-4 py-3 align-top whitespace-nowrap text-muted-foreground">
                        {formatParisDate(row.createdAt)}
                      </td>
                      <td className="px-4 py-3 align-top font-mono text-xs text-foreground break-all max-w-[120px]">
                        {row.visitorId || '—'}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <PaymentStatusBadge row={row} />
                      </td>
                      {questions.map((q, i) => (
                        <td
                          key={`${row.id}-q-${i}`}
                          className="px-4 py-3 align-top text-foreground max-w-[220px]"
                        >
                          {formatAnswerValue(q, row.answersByIndex[i])}
                        </td>
                      ))}
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap text-muted-foreground">
                        {formatPrice(effectiveResponseTotalHT(row))}
                      </td>
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap font-medium">
                        {formatPrice(effectiveResponseTotalTTC(row))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </>
  );
};

export default HubupNotificationFormResponses;
